import { useState, useEffect, useRef } from 'react'
import { useNotifications } from '../context/NotificationContext'
import { useAuth } from '../context/AuthContext'
import { API_BASE } from '../utils/api'

type IndStatus = 'live' | 'connected' | 'running' | 'active' | 'open'
  | 'delayed' | 'idle' | 'pre-open'
  | 'disconnected' | 'stopped' | 'error' | 'closed'

interface Indicator {
  key: string
  label: string
  status: IndStatus
  detail?: string
}

const STATUS_COLOR: Record<string, string> = {
  live: '#8ab82a', connected: '#8ab82a', running: '#8ab82a',
  active: '#8ab82a', open: '#8ab82a',
  delayed: '#eab308', idle: '#eab308', 'pre-open': '#eab308',
  disconnected: '#d4495a', stopped: '#d4495a', error: '#d4495a',
  closed: '#64748b',
}

const STATUS_LABEL: Record<string, string> = {
  live: 'LIVE', connected: 'OK', running: 'RUN', active: 'OK', open: 'OPEN',
  delayed: 'LAG', idle: 'IDLE', 'pre-open': 'PRE',
  disconnected: 'DOWN', stopped: 'OFF', error: 'ERR', closed: 'CLOSED',
}

const isPulse = (s: IndStatus) =>
  ['live', 'connected', 'running', 'active', 'open'].includes(s)

const isCritical = (s: IndStatus) =>
  ['disconnected', 'stopped', 'error'].includes(s)

const isWarn = (s: IndStatus) =>
  ['delayed', 'idle', 'pre-open'].includes(s)

function getMarketStatus(): IndStatus {
  const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }))
  const mins = ist.getHours() * 60 + ist.getMinutes()
  const dow = ist.getDay() // 0=Sun, 6=Sat
  if (dow === 0 || dow === 6) return 'closed'
  if (mins >= 9 * 60 + 15 && mins <= 15 * 60 + 30) return 'open'
  if (mins >= 9 * 60 && mins < 9 * 60 + 15) return 'pre-open'
  return 'closed'
}

// ── Single row ────────────────────────────────────────────────
function IndicatorRow({ ind }: { ind: Indicator }) {
  const color = STATUS_COLOR[ind.status] ?? '#64748b'
  const badge = STATUS_LABEL[ind.status] ?? ind.status.toUpperCase()
  const pulse = isPulse(ind.status)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0' }}>
      <span style={{
        flexShrink: 0, width: 7, height: 7, borderRadius: '50%',
        background: color,
        boxShadow: pulse ? `0 0 5px ${color}` : 'none',
        animation: pulse ? 'pulse-dot 2s ease-in-out infinite' : 'none',
      }} />
      <span style={{
        flex: 1, fontSize: '0.6875rem', fontWeight: 600,
        color: 'var(--text-secondary)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {ind.label}
        {ind.detail && (
          <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: '0.25rem' }}>
            {ind.detail}
          </span>
        )}
      </span>
      <span style={{
        flexShrink: 0, fontSize: '0.5625rem', fontWeight: 800,
        letterSpacing: '0.05em', color,
        background: `${color}18`, border: `1px solid ${color}44`,
        borderRadius: '3px', padding: '0.1rem 0.35rem',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        {badge}
      </span>
    </div>
  )
}

// ── System Status Panel ───────────────────────────────────────
export function SystemStatus() {
  const { addNotification } = useNotifications()
  const { token } = useAuth()

  const [indicators, setIndicators] = useState<Indicator[]>([
    { key: 'market_h', label: 'Market Hours', status: getMarketStatus() },
    { key: 'engine',   label: 'Trading Engine', status: 'stopped', detail: 'VPS' },
    { key: 'api',      label: 'API Worker',     status: 'stopped', detail: 'Cloudflare' },
  ])

  // Track previous statuses to detect changes and fire notifications
  const prevRef = useRef<Record<string, IndStatus>>({})

  useEffect(() => {
    const authHeaders: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {}

    const fetchStatuses = async () => {
      const newIndicators: Indicator[] = [
        { key: 'market_h', label: 'Market Hours', status: getMarketStatus() },
      ]

      // ── 1. Cloudflare API Worker ──────────────────────────────
      try {
        const apiRes = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(5000) })
        if (apiRes.ok) {
          newIndicators.push({ key: 'api', label: 'API Worker', status: 'running', detail: 'Cloudflare' })
        } else {
          newIndicators.push({ key: 'api', label: 'API Worker', status: 'error', detail: 'Cloudflare' })
        }
      } catch {
        newIndicators.push({ key: 'api', label: 'API Worker', status: 'disconnected', detail: 'Offline' })
      }

      // ── 2. VPS Trading Engine + Live Broker Status ────────────
      // /api/vps/status is the SINGLE SOURCE OF TRUTH:
      // It queries VPS /health AND /brokers in parallel, returning live
      // in-memory state (sessionId, irisConnected, apolloConnected, status).
      // It does NOT read stale Cloudflare D1 database rows.
      try {
        const vpsStatusRes = await fetch(`${API_BASE}/api/vps/status`, {
          headers: authHeaders,
          signal: AbortSignal.timeout(6000),
        })

        if (vpsStatusRes.ok) {
          const vpsStatus = await vpsStatusRes.json() as any

          // Trading Engine process health
          if (vpsStatus.engineOnline) {
            const uptimeSec = vpsStatus.uptime != null ? Math.round(vpsStatus.uptime) : null
            newIndicators.push({
              key: 'engine',
              label: 'Trading Engine',
              status: 'running',
              detail: uptimeSec != null ? `VPS • ${uptimeSec}s up` : 'VPS',
            })
          } else {
            newIndicators.push({ key: 'engine', label: 'Trading Engine', status: 'error', detail: 'VPS offline' })
          }

          // Per-broker live runtime status (sourced from BrokerConnection in-memory state)
          const liveBrokers: any[] = Array.isArray(vpsStatus.brokers) ? vpsStatus.brokers : []

          if (liveBrokers.length === 0 && vpsStatus.engineOnline) {
            newIndicators.push({ key: 'broker-none', label: 'No Brokers', status: 'stopped', detail: 'not registered' })
          }

          for (const b of liveBrokers) {
            // Map VPS runtime status → IndStatus
            let brokerStatus: IndStatus
            if (b.status === 'connected') brokerStatus = 'connected'
            else if (b.status === 'connecting') brokerStatus = 'delayed'
            else if (b.status === 'error') brokerStatus = 'error'
            else brokerStatus = 'disconnected'

            // Detail line: show last error if in error state, else show session indicators
            let detail: string
            if (brokerStatus === 'error' && b.lastError) {
              // Truncate long error messages for the compact sidebar
              const errSnippet = b.lastError.length > 32 ? b.lastError.slice(0, 32) + '…' : b.lastError
              detail = errSnippet
            } else {
              // Show session indicator flags
              const sToken = b.hasSessionToken ? '🔑' : '—'
              const sId    = b.hasSessionId   ? '✓'  : '✗'
              detail = `${sToken} sid:${sId}`
            }

            newIndicators.push({
              key: `broker-${b.id}`,
              label: b.name || b.clientId || b.id,
              status: brokerStatus,
              detail,
            })

            // WebSocket status per broker (authoritative readyState from VPS memory)
            const irisUp   = b.irisConnected === true
            const apolloUp = b.apolloConnected === true
            const wsStatus: IndStatus = irisUp && apolloUp ? 'connected'
              : irisUp || apolloUp ? 'delayed'
              : b.status === 'connected' ? 'delayed'   // connected but WS dropped = warn
              : 'closed'

            newIndicators.push({
              key: `ws-${b.id}`,
              label: 'WebSocket',
              status: wsStatus,
              detail: `IRIS ${irisUp ? '✓' : '✗'} · Apollo ${apolloUp ? '✓' : '✗'}`,
            })
          }

        } else {
          // VPS proxy call failed
          newIndicators.push({ key: 'engine', label: 'Trading Engine', status: 'disconnected', detail: 'VPS Offline' })
          newIndicators.push({ key: 'ws',     label: 'WebSocket',      status: 'stopped',      detail: 'VPS unreachable' })
        }
      } catch {
        // Network-level failure reaching the CF worker
        newIndicators.push({ key: 'engine', label: 'Trading Engine', status: 'disconnected', detail: 'VPS Offline' })
        newIndicators.push({ key: 'ws',     label: 'WebSocket',      status: 'stopped',      detail: 'VPS unreachable' })
      }

      setIndicators(_prev => {
        // Detect status changes and fire notifications
        newIndicators.forEach(ind => {
          const old = prevRef.current[ind.key]
          if (old !== undefined && old !== ind.status) {
            if (isCritical(ind.status)) {
              addNotification(
                `⚠ ${ind.label} — ${STATUS_LABEL[ind.status]}`,
                `${ind.label} is now ${ind.status.toUpperCase()}.`,
                'system'
              )
            } else if (isPulse(ind.status) && (isCritical(old) || isWarn(old))) {
              addNotification(
                `✅ ${ind.label} — Recovered`,
                `${ind.label} is back to ${STATUS_LABEL[ind.status]}.`,
                'system'
              )
            }
          }
          prevRef.current[ind.key] = ind.status
        })
        return newIndicators
      })
    }

    fetchStatuses()
    const id = setInterval(fetchStatuses, 12000)
    return () => clearInterval(id)
  }, [token, addNotification])

  const criticalCount = indicators.filter(i => isCritical(i.status)).length
  const warnCount     = indicators.filter(i => isWarn(i.status)).length
  const overallColor  = criticalCount > 0 ? '#d4495a' : warnCount > 0 ? '#eab308' : '#8ab82a'
  const overallLabel  = criticalCount > 0 ? `${criticalCount} DOWN` : warnCount > 0 ? 'WARN' : 'ALL OK'

  return (
    <div style={{
      margin: '0.5rem 0.625rem 0',
      padding: '0.625rem 0.75rem',
      background: 'rgba(14,26,50,0.55)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <span style={{
          fontSize: '0.625rem', fontWeight: 800,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}>
          System Status
        </span>
        <span style={{
          fontSize: '0.5625rem', fontWeight: 800, letterSpacing: '0.06em',
          color: overallColor, background: `${overallColor}18`,
          border: `1px solid ${overallColor}44`,
          borderRadius: '3px', padding: '0.1rem 0.4rem',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {overallLabel}
        </span>
      </div>

      {/* Indicators */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {indicators.map(ind => <IndicatorRow key={ind.key} ind={ind} />)}
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '0.5rem', paddingTop: '0.375rem',
        borderTop: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
          ⟳ 12s · live VPS state
        </span>
        <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
          {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })} IST
        </span>
      </div>
    </div>
  )
}
